import { isValidCnpj } from '@/shared/lib/cnpj';
import { isValidCpf } from '@/shared/lib/cpf';
import { maskCnpj, maskCpf } from '@/shared/lib/input-masks';
import type { PassbookAddField } from '@/shared/types/passbook';
import type { PublicInvitePassbook } from '@/features/public-invite/api/use-public-invite';
import { resolveInviteFieldPlan, type InviteFieldPlan } from '@/features/public-invite/model/invite-variant';

/** Identificadores com que dá para entrar (o código vai para o celular BR do cadastro). */
export type LoginIdentity = 'cpf' | 'cnpj';

export interface ParticipantFormPlan {
  /** O mesmo plano do cadastro público: Meus dados mostra o que o cadastro coletou. */
  plan: InviteFieldPlan;
  /** `null` = a loja usa placa, telefone internacional ou código gerado: sem login por aqui. */
  identity: LoginIdentity | null;
  identityLabel: string;
  /** Campos extras do cadastro da loja (só nas lojas em que o cadastro os mostra). */
  customFields: PassbookAddField[];
  /** Slugs de `fields` que a tela edita (extras + texto do campo opcional + estado/cidade). */
  editableSlugs: string[];
  /** Sufixo " da Criança" (Brincarte): identidade, nascimento e gênero são da criança. */
  childSuffix: string;
}

export function participantFormPlan(passbook: PublicInvitePassbook): ParticipantFormPlan {
  const plan = resolveInviteFieldPlan(passbook);
  const identity: LoginIdentity | null =
    plan.identity === 'cpf' ? 'cpf' : plan.identity === 'cnpj' ? 'cnpj' : null;
  const customFields = plan.showCustomFields ? (passbook.addFields ?? []) : [];
  const editableSlugs = customFields
    .filter((f) => !f.highlight && !f.disabled)
    .flatMap((f) => (f.optionalField ? [f.slug, `optionalField_${f.slug}`] : [f.slug]));
  if (plan.showStateCity) editableSlugs.push('state', 'city');
  return {
    plan,
    identity,
    identityLabel: identity === 'cnpj' ? 'CNPJ' : passbook.fieldUniqueIdLabel || 'CPF',
    customFields,
    editableSlugs,
    childSuffix: plan.childLabels ? ' da Criança' : '',
  };
}

export function maskIdentity(identity: LoginIdentity | null, value: string): string {
  if (identity === 'cnpj') return maskCnpj(value);
  if (identity === 'cpf') return maskCpf(value);
  return value;
}

export function isValidIdentity(identity: LoginIdentity, value: string): boolean {
  return identity === 'cnpj' ? isValidCnpj(value) : isValidCpf(value);
}

/** Há regras de fidelidade ou termos para mostrar (bolinha Informações / Regras)? */
export function hasRulesOrTerms(passbook: PublicInvitePassbook): boolean {
  return (
    passbook.loyaltyRules.length > 0 || Boolean(passbook.termsAndConditions?.trim() || passbook.linkTermsConditions)
  );
}
